import { useContext, useCallback, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import homeScreenSplash from "../../assets/homescreen-splash.jpg";
import Layout from "../../components/layout/Layout";
import { useSyncSelectedClient } from "../../Hooks/useSyncSelected";
import { AuthContext } from "../../state/AuthContext";
import ClientsMapPanel from "../web/ClientsMapPanel";
import { apiRequest } from "../../api/baseApi";
import { capturePanelFailure } from "../../config/capturePanelFailure";
import { parseJsonOrThrow, withResponseContext } from "../../api/responseUtils";
import { wasAuthFailureReported } from "../../api/authFailureMarker";
import { canShowHomeClientsMap } from "../../utils/clientProfileAccess";
import "./SelectClientHomepage.css";

const appStoreUrl = process.env.REACT_APP_APPSTORE_URL;
const playStoreUrl = process.env.REACT_APP_PLAYSTORE_URL;

// #731: the admin routes live on v1 — measured against the deployed API, where
// v1 answers 401 (exists, auth-gated) and v2/v3 answer 404, with a bogus v1
// path also answering 404 so the pair discriminates.
const ADMIN_API = { version: "v1" };

// Failures are reported through capturePanelFailure, never captureError: the
// shared helper owns the de-duplication, skipping 5xx and the network/auth
// markers because baseApi has captured those centrally by the time we see
// them, and capturing the rest. It also puts panel/surface/op in TAGS, which
// is the only level captureError forwards.
const ADMIN_SURFACE = "home-admin";

// Classify on the markers baseApi sets, never on message text, and render a
// fixed sentence of ours rather than interpolating an unknown error's prose.
// baseApi's own transport message already begins "Network error:", so
// prefixing it would double the phrase, and a marked AUTH failure must not be
// reported to the admin as a connection problem.
const RESET_MFA_FAILED = {
  auth: "Your session has expired. Please sign in again and retry the reset.",
  network: "Could not reach the server. Please check your connection and try again.",
  unknown: "The MFA reset could not be completed. Please try again.",
};

const resetMfaFailureMessage = (err) => {
  if (err?.isAuthFailure) return RESET_MFA_FAILED.auth;
  if (err?.isNetworkFailure) return RESET_MFA_FAILED.network;
  return RESET_MFA_FAILED.unknown;
};

// One plain-language sentence per action. A caregiver-facing admin reading
// this needs to know WHICH action failed and that it can be retried — never a
// status code, never a raw trace.
const ACTION_FAILED = {
  report: "The user report could not be downloaded. Please try again.",
  auditView: "The audit log could not be loaded. Please try again.",
  auditCsv: "The audit CSV could not be exported. Please try again.",
};

/**
 * One action's failure, rendered beside the control it belongs to. Each of the
 * three admin actions renders its own alert from its own key; they are
 * independent by design and must not be consolidated into a single banner.
 */
function AdminActionError({ message, action }) {
  if (!message) return null;
  return (
    <div
      data-testid="admin-action-error"
      data-action={action}
      role="alert"
      style={{
        margin: "8px 0",
        padding: "10px 14px",
        border: "1px solid #c0392b",
        borderRadius: "6px",
        background: "#fdeaea",
        color: "#922b21",
        textAlign: "left",
      }}
    >
      {message}
    </div>
  );
}

function SelectClientHomepage() {
  useSyncSelectedClient();
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  // In-flight guard for the CSV export, matching `loadingAudit` and
  // `downloading` on the other two. Per-action state keeps the three actions
  // from colliding; this keeps the export from colliding with itself, since a
  // disabled button cannot start a second overlapping attempt.
  const [exportingCsv, setExportingCsv] = useState(false);
  const [auditLogs, setAuditLogs] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [resetMfaEmail, setResetMfaEmail] = useState("");
  const [resettingMfa, setResettingMfa] = useState(false);
  const [resetMfaResult, setResetMfaResult] = useState(null);
  const [expandedLogId, setExpandedLogId] = useState(null);
  // #731: the three actions below used to fail silently. Reset MFA keeps its
  // own inline result — it was already reporting. Errors are PER ACTION. Each of the three admin actions owns one key and
  // writes only that key, so a slow failure cannot land on top of a different
  // action's later result, and starting one action never clears another's
  // error while it is still true.
  const [adminErrors, setAdminErrors] = useState({});
  const setAdminError = useCallback((key, message) => {
    setAdminErrors((prev) => (prev[key] === message ? prev : { ...prev, [key]: message }));
  }, []);
  const clearAdminError = useCallback((key) => {
    setAdminErrors((prev) => (key in prev ? { ...prev, [key]: null } : prev));
  }, []);
  const showClientsMap = canShowHomeClientsMap(currentUser);

  const handleViewAuditLog = useCallback(async () => {
    setLoadingAudit(true);
    clearAdminError("auditView");
    try {
      const response = await apiRequest("admin/audit-logs", ADMIN_API);
      // parseJsonOrThrow attaches response.status and baseApi's auth-failure
      // marker to the thrown error. capturePanelFailure de-duplicates on
      // exactly those two fields, so an error thrown without them would make
      // it re-report the 5xx and refresh-failed 401s baseApi already sent.
      const logs = await parseJsonOrThrow(response, "Audit log");
      setAuditLogs(logs);
    } catch (err) {
      capturePanelFailure(err, { panel: "audit-log", surface: ADMIN_SURFACE, op: "view" });
      setAdminError("auditView", ACTION_FAILED.auditView);
    } finally {
      setLoadingAudit(false);
    }
  }, []);

  const handleExportAuditCsv = useCallback(async () => {
    setExportingCsv(true);
    clearAdminError("auditCsv");
    try {
      const response = await apiRequest("admin/audit-logs?format=csv", ADMIN_API);
      // A blob endpoint never reaches parseJsonOrThrow (the success path wants
      // response.blob()), so attach the same context by hand — see above.
      if (!response.ok) {
        throw withResponseContext(
          new Error(`audit CSV export failed (${response.status})`),
          response,
        );
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      capturePanelFailure(err, { panel: "audit-log", surface: ADMIN_SURFACE, op: "export_csv" });
      setAdminError("auditCsv", ACTION_FAILED.auditCsv);
    } finally {
      setExportingCsv(false);
    }
  }, [clearAdminError, setAdminError]);


  const handleResetMfa = useCallback(async () => {
    if (!resetMfaEmail.trim()) return;
    setResettingMfa(true);
    setResetMfaResult(null);
    try {
      const response = await apiRequest("admin/reset-mfa", {
        ...ADMIN_API,
        method: "POST",
        body: { targetEmail: resetMfaEmail.trim() },
      });
      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        // A body we cannot parse is OUR API misbehaving, and nothing central
        // reports it — baseApi saw a completed response. Carry the response
        // context so the shared helper can classify it.
        throw withResponseContext(parseErr, response);
      }
      if (!response.ok && (response.status === 401 || wasAuthFailureReported(response))) {
        // baseApi RETURNS a 401 rather than throwing, in two shapes. After a
        // failed refresh it is MARKED, and withResponseContext carries the
        // marker so the admin reads "session expired" and the shared helper
        // skips what baseApi already reported. After a refresh that SUCCEEDED
        // it is UNMARKED — a real authorization answer to a fresh token that
        // baseApi deliberately does not capture — and the repository contract
        // (baseApi.reactiveAuthMarker.test) is that the caller captures it.
        // Neither is an admin typo, so neither may fall to the inline branch.
        throw withResponseContext(new Error("reset-mfa auth failure"), response);
      }
      if (!response.ok) {
        // Deliberately NOT a Sentry event. A 4xx here is an EXPECTED outcome —
        // "no such user" is an admin mistyping an address — and the UI already
        // shows the server's message. Capturing every typo would be noise on
        // exactly the filter a real reset failure needs to be visible on. A
        // 5xx reaching here was already captured centrally by baseApi.
        setResetMfaResult({ type: "error", message: data.message || "Reset failed" });
      } else if (!data.success) {
        // A 2xx that did not do the thing. baseApi counts this a SUCCESS, so
        // this branch is the only place it can be reported.
        capturePanelFailure(
          new Error("reset-mfa returned success: false on a 2xx response"),
          { panel: "reset-mfa", surface: ADMIN_SURFACE, op: "reset" },
        );
        setResetMfaResult({ type: "error", message: data.message || "Reset failed" });
      } else {
        setResetMfaResult({ type: "success", message: data.message });
        setResetMfaEmail("");
      }
    } catch (err) {
      // The helper skips what baseApi already captured (network and auth
      // markers, 5xx) and captures what it did not — notably a parse failure,
      // which carries the response's status but neither marker.
      capturePanelFailure(err, { panel: "reset-mfa", surface: ADMIN_SURFACE, op: "reset" });
      setResetMfaResult({ type: "error", message: resetMfaFailureMessage(err) });
    } finally {
      setResettingMfa(false);
    }
  }, [resetMfaEmail]);

  const handleDownloadReport = useCallback(async () => {
    setDownloading(true);
    clearAdminError("report");
    try {
      const response = await apiRequest("admin/users/report", ADMIN_API);
      if (!response.ok) {
        throw withResponseContext(
          new Error(`user report download failed (${response.status})`),
          response,
        );
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `user-report-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      capturePanelFailure(err, { panel: "user-report", surface: ADMIN_SURFACE, op: "download" });
      setAdminError("report", ACTION_FAILED.report);
    } finally {
      setDownloading(false);
    }
  }, []);

  const AdminSection = () => (
    <div
      style={{
        padding: "24px 48px",
        backgroundColor: "#D9D9D9",
        margin: "0 auto",
        width: "600px",
      }}
    >
      <h2 style={{ fontWeight: "bold" }}>Assignment Center</h2>
      <div>
        <h5>Manage which clients are assigned to which caregivers.</h5>
        <br />
        <button
          className="btn-primary-lg"
          onClick={() => navigate("/assignment_center")}
        >
          Lets go
        </button>
      </div>
      {currentUser?.isSuperAdmin && (
        <>
          {/* #731: the three actions below used to end in console.error, so a
              failure and a success looked identical. Each action now renders
              its own AdminActionError from its own key, and clears only that
              key when it is retried; they are independent by design. */}
          <div style={{ marginTop: "24px", borderTop: "1px solid #bbb", paddingTop: "20px" }}>
            <h2 style={{ fontWeight: "bold" }}>User Report</h2>
            <AdminActionError message={adminErrors.report} action="report" />
            <h5>Download a CSV report of all accounts, assignments, and contacts.</h5>
            <br />
            <button
              className="btn-primary-lg"
              onClick={handleDownloadReport}
              disabled={downloading}
            >
              {downloading ? "Downloading..." : "Download User Report"}
            </button>
          </div>
          <div style={{ marginTop: "24px", borderTop: "1px solid #bbb", paddingTop: "20px" }}>
            <h2 style={{ fontWeight: "bold" }}>Audit Log</h2>
            <AdminActionError message={adminErrors.auditView} action="auditView" />
            <AdminActionError message={adminErrors.auditCsv} action="auditCsv" />
            <h5>View admin action history — deletions, password resets, and account changes.</h5>
            <p style={{ fontSize: "12px", color: "#666", margin: "4px 0 12px" }}>
              SOC 2 / HIPAA compliant: captures who, what, when, source IP, device, target, and outcome.
            </p>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <button
                className="btn-primary-lg"
                onClick={handleViewAuditLog}
                disabled={loadingAudit}
              >
                {loadingAudit ? "Loading..." : "View Audit Log"}
              </button>
              <button
                className="btn-primary-lg"
                onClick={handleExportAuditCsv}
                disabled={exportingCsv}
                style={{ background: "#2980b9" }}
              >
                {exportingCsv ? "Exporting..." : "Export Full Audit CSV"}
              </button>
            </div>
            {auditLogs && (
              <div style={{ marginTop: "16px", maxHeight: "500px", overflowY: "auto", overflowX: "auto", textAlign: "left" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", minWidth: "950px" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #999", background: "#f5f5f5", position: "sticky", top: 0, zIndex: 1 }}>
                      <th style={{ padding: "8px 6px", textAlign: "left", width: "28px" }}></th>
                      <th style={{ padding: "8px 6px", textAlign: "left" }}>Action</th>
                      <th style={{ padding: "8px 6px", textAlign: "left" }}>By (ID)</th>
                      <th style={{ padding: "8px 6px", textAlign: "left" }}>Target (ID)</th>
                      <th style={{ padding: "8px 6px", textAlign: "left" }}>Date & Time</th>
                      <th style={{ padding: "8px 6px", textAlign: "left" }}>IP Address</th>
                      <th style={{ padding: "8px 6px", textAlign: "left" }}>Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log) => (
                      <Fragment key={log.id}>
                        <tr
                          style={{ borderBottom: expandedLogId === log.id ? "none" : "1px solid #ccc", cursor: "pointer" }}
                          onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                        >
                          <td style={{ padding: "6px", fontSize: "10px", color: "#888" }}>
                            {expandedLogId === log.id ? "▼" : "▶"}
                          </td>
                          <td style={{ padding: "6px" }}>{log.action}</td>
                          <td style={{ padding: "6px" }}>
                            <span style={{ fontFamily: "monospace", fontSize: "11px" }}>#{log.performedBy}</span>
                          </td>
                          <td style={{ padding: "6px" }}>
                            {log.targetUserId
                              ? <span style={{ fontFamily: "monospace", fontSize: "11px" }}>#{log.targetUserId}</span>
                              : "-"}
                          </td>
                          <td style={{ padding: "6px", whiteSpace: "nowrap" }}>
                            {new Date(log.performedAt).toLocaleString()}
                          </td>
                          <td style={{ padding: "6px", fontFamily: "monospace", fontSize: "11px" }}>
                            {log.ipAddress || "-"}
                          </td>
                          <td style={{ padding: "6px" }}>
                            <span style={{
                              display: "inline-block",
                              padding: "1px 8px",
                              borderRadius: "10px",
                              fontSize: "11px",
                              fontWeight: 600,
                              background: log.outcome === "success" ? "#e6f4ea" : "#fce8e6",
                              color: log.outcome === "success" ? "#1e7e34" : "#c62828",
                            }}>
                              {log.outcome || "success"}
                            </span>
                          </td>
                        </tr>
                        {expandedLogId === log.id && (
                          <tr key={`${log.id}-detail`} style={{ borderBottom: "1px solid #ccc", background: "#fafafa" }}>
                            <td colSpan={7} style={{ padding: "8px 6px 8px 34px", fontSize: "11px", color: "#555", lineHeight: "1.8" }}>
                              <strong>Performed By:</strong>{" "}
                              <span style={{ fontFamily: "monospace", fontSize: "10px" }}>
                                {log.performedByEmail || "-"}
                              </span>
                              <br />
                              <strong>Target:</strong>{" "}
                              <span style={{ fontFamily: "monospace", fontSize: "10px" }}>
                                {log.targetUserEmail || "-"}
                              </span>
                              <br />
                              <strong>User Agent:</strong>{" "}
                              <span style={{ fontFamily: "monospace", fontSize: "10px", wordBreak: "break-all" }}>
                                {log.userAgent || "Not captured"}
                              </span>
                              {log.metadata && Object.keys(log.metadata).length > 0 && (
                                <>
                                  <br />
                                  <strong>Metadata:</strong>{" "}
                                  <span style={{ fontFamily: "monospace", fontSize: "10px" }}>
                                    {JSON.stringify(log.metadata)}
                                  </span>
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
                {auditLogs.length === 0 && (
                  <p style={{ textAlign: "center", color: "#888", marginTop: "12px" }}>
                    No audit logs found.
                  </p>
                )}
              </div>
            )}
          </div>
          <div style={{ marginTop: "24px", borderTop: "1px solid #bbb", paddingTop: "20px" }}>
            <h2 style={{ fontWeight: "bold" }}>Reset User MFA</h2>
            <h5>Remove MFA for a user so they can set it up again on next login.</h5>
            <div style={{ display: "flex", gap: "12px", alignItems: "center", marginTop: "12px" }}>
              <input
                type="email"
                placeholder="User email address"
                value={resetMfaEmail}
                onChange={(e) => setResetMfaEmail(e.target.value)}
                style={{
                  padding: "8px 12px",
                  fontSize: "14px",
                  border: "1px solid #999",
                  borderRadius: "6px",
                  flex: 1,
                }}
              />
              <button
                className="btn-primary-lg"
                onClick={handleResetMfa}
                disabled={resettingMfa || !resetMfaEmail.trim()}
                style={{ background: "#c0392b", whiteSpace: "nowrap" }}
              >
                {resettingMfa ? "Resetting..." : "Reset MFA"}
              </button>
            </div>
            {resetMfaResult && (
              <p style={{
                marginTop: "8px",
                fontSize: "13px",
                color: resetMfaResult.type === "success" ? "#1e7e34" : "#c62828",
                fontWeight: 600,
              }}>
                {resetMfaResult.message}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );

  const ContactSection = () => (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "40px 20px" }}>
      {/* Hero Section */}
      <div style={{ textAlign: "center", marginBottom: "60px" }}>
        <h2
          style={{
            fontSize: "42px",
            fontWeight: "700",
            color: "#2c3e50",
            marginBottom: "20px",
            lineHeight: "1.2",
          }}
        >
          CompassCare is Now Available on iOS and Android!
        </h2>
        <p
          style={{
            fontSize: "20px",
            color: "#555",
            maxWidth: "900px",
            margin: "0 auto 40px",
            lineHeight: "1.6",
          }}
        >
          We&apos;re excited to announce that CompassCare has officially
          launched and is now available for download on both the Apple App Store
          and Google Play Store.
        </p>

        {/* Download Badges */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "30px",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <a
            href={appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              transition: "transform 0.2s, box-shadow 0.2s",
              display: "inline-block",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-5px)";
              e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              style={{ height: "70px" }}
            />
          </a>
          <a
            href={playStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              transition: "transform 0.2s, box-shadow 0.2s",
              display: "inline-block",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-5px)";
              e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
              alt="Get it on Google Play"
              style={{ height: "70px" }}
            />
          </a>
        </div>
        <p style={{ fontSize: "14px", color: "#888", marginTop: "10px" }}>
          Version 1.0.0 | Released 2025 | © Gray Matters Alliance
        </p>
      </div>

      {/* Main Value Prop */}
      <div
        style={{
          backgroundColor: "#f8f9fa",
          padding: "40px",
          borderRadius: "12px",
          marginBottom: "50px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        }}
      >
        <h3
          style={{
            fontSize: "32px",
            fontWeight: "700",
            color: "#2c3e50",
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          Experience the Future of Healthcare Communication
        </h3>
        <p
          style={{
            fontSize: "18px",
            color: "#555",
            textAlign: "center",
            lineHeight: "1.7",
            maxWidth: "900px",
            margin: "0 auto",
          }}
        >
          CompassCare is a secure, HIPAA-compliant platform designed to connect
          healthcare providers, caregivers, and clients through high-quality
          video calls and real-time communication. Built for assisted living and
          home care environments, CompassCare streamlines care coordination with
          professional-grade tools and enterprise-level security.
        </p>
      </div>

      {/* Why Choose CompassCare */}
      <div style={{ marginBottom: "50px" }}>
        <h3
          style={{
            fontSize: "32px",
            fontWeight: "700",
            color: "#2c3e50",
            textAlign: "center",
            marginBottom: "40px",
          }}
        >
          Why Choose CompassCare?
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "25px",
          }}
        >
          {[
            {
              icon: "✅",
              title: "Officially Approved",
              desc: "Available now on both major app stores",
            },
            {
              icon: "🔒",
              title: "HIPAA Compliant",
              desc: "End-to-end encrypted video calls for patient privacy",
            },
            {
              icon: "🔔",
              title: "Real-time Notifications",
              desc: "Instant connectivity when you need it most",
            },
            {
              icon: "🛡️",
              title: "Advanced Security",
              desc: "Multi-factor authentication for enterprise protection",
            },
            {
              icon: "📱",
              title: "Cross-Platform",
              desc: "Seamless experience across iOS, Android, and web",
            },
            {
              icon: "👥",
              title: "User-Friendly",
              desc: "Intuitive interface that's easy for everyone to use",
            },
            {
              icon: "⭐",
              title: "Trusted Nationwide",
              desc: "Used by healthcare providers across the country",
            },
            {
              icon: "📊",
              title: "Complete History",
              desc: "Comprehensive call management and reporting",
            },
            {
              icon: "⚡",
              title: "24/7 Reliable",
              desc: "Dependable performance anytime, anywhere",
            },
          ].map((feature, index) => (
            <div
              key={index}
              style={{
                backgroundColor: "white",
                padding: "25px",
                borderRadius: "10px",
                boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
                transition: "all 0.3s",
                cursor: "default",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.12)";
                e.currentTarget.style.transform = "translateY(-5px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 3px 10px rgba(0,0,0,0.08)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div
                style={{
                  fontSize: "36px",
                  marginBottom: "15px",
                  textAlign: "center",
                }}
              >
                {feature.icon}
              </div>
              <h4
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#2c3e50",
                  marginBottom: "10px",
                  textAlign: "center",
                }}
              >
                {feature.title}
              </h4>
              <p
                style={{
                  fontSize: "15px",
                  color: "#666",
                  lineHeight: "1.5",
                  textAlign: "center",
                }}
              >
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Key Features */}
      <div
        style={{
          backgroundColor: "#f8f9fa",
          padding: "40px",
          borderRadius: "12px",
          marginBottom: "50px",
        }}
      >
        <h3
          style={{
            fontSize: "32px",
            fontWeight: "700",
            color: "#2c3e50",
            textAlign: "center",
            marginBottom: "30px",
          }}
        >
          Key Features at a Glance
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "15px",
            maxWidth: "1000px",
            margin: "0 auto",
          }}
        >
          {[
            "Secure, high-quality video calling with one-tap initiation",
            "Real-time status updates and notifications",
            "Role-based access for caregivers, providers, and clients",
            "Detailed call history, notes, and reporting",
            "Efficient contact management and search",
            "Enterprise-grade authentication and encrypted data transmission",
            "Optimized for speed, battery life, and low-bandwidth environments",
            "Ready for EHR and care management system integration",
            "Shared care plans and team communication tools",
            "Consistent experience across all devices",
            "HIPAA-compliant architecture and privacy protections",
            "Emergency support and rapid response features",
            "Accessibility options for all users",
            "Push notifications for calls, reminders, and updates",
          ].map((feature, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                padding: "10px",
              }}
            >
              <span
                style={{ color: "#72795B", fontSize: "18px", minWidth: "20px" }}
              >
                ✓
              </span>
              <span
                style={{ fontSize: "15px", color: "#555", lineHeight: "1.5" }}
              >
                {feature}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* How It Works */}
      <div
        style={{
          backgroundColor: "white",
          padding: "40px",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          marginBottom: "50px",
        }}
      >
        <h3
          style={{
            fontSize: "28px",
            fontWeight: "700",
            color: "#2c3e50",
            textAlign: "center",
            marginBottom: "30px",
          }}
        >
          Getting Started is Easy
        </h3>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "40px",
            maxWidth: "900px",
            margin: "0 auto",
            flexWrap: "wrap",
          }}
        >
          {[
            {
              num: "1",
              text: "Open this website on your mobile device",
            },
            { num: "2", text: "Select the download link for your platform" },
            { num: "3", text: "Sign in to your account and start connecting" },
          ].map((step, index) => (
            <div
              key={index}
              style={{
                flex: "1",
                minWidth: "200px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  backgroundColor: "#72795B",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "28px",
                  fontWeight: "700",
                  margin: "0 auto 15px",
                }}
              >
                {step.num}
              </div>
              <p style={{ fontSize: "16px", color: "#555", lineHeight: "1.5" }}>
                {step.text}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div style={{ textAlign: "center", marginTop: "60px" }}>
        <h3
          style={{
            fontSize: "36px",
            fontWeight: "700",
            color: "#2c3e50",
            marginBottom: "20px",
          }}
        >
          Ready to Transform Your Healthcare Communication?
        </h3>
        <p
          style={{
            fontSize: "20px",
            color: "#555",
            marginBottom: "30px",
            lineHeight: "1.6",
          }}
        >
          Download CompassCare today and discover a better way to connect,
          coordinate, and deliver care.
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "30px",
            flexWrap: "wrap",
          }}
        >
          <a
            href={appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              transition: "transform 0.2s, box-shadow 0.2s",
              display: "inline-block",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-5px)";
              e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              style={{ height: "70px" }}
            />
          </a>
          <a
            href={playStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              transition: "transform 0.2s, box-shadow 0.2s",
              display: "inline-block",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-5px)";
              e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
              alt="Get it on Google Play"
              style={{ height: "70px" }}
            />
          </a>
        </div>
        <p
          style={{
            fontSize: "24px",
            fontWeight: "600",
            color: "#72795B",
            marginTop: "40px",
            fontStyle: "italic",
          }}
        >
          CompassCare – Connecting Care, Enhancing Lives
        </p>
      </div>
    </div>
  );

  // Thomas Notes: Added optional chaining to prevent "Cannot read properties of undefined" errors
  const content = (
    <div
      style={
        currentUser?.isContact
          ? { padding: "40px 20px" }
          : { marginTop: "150px" }
      }
    >
      <img
        src={homeScreenSplash}
        alt="Compass Care by Gray Matters Alliance"
        className="splash-image"
      />
      <h1 className="intro">Welcome to Compass Care</h1>
      {showClientsMap ? (
        // #685: the map renders ON the page (no button, no command-center
        // shell). #747: plain caregivers only for now — super_admin and
        // caregiver_admin get the Assignment Center below instead. Fail-closed
        // via canShowHomeClientsMap. Lives outside /web/*, so a production
        // build without the command-center flag still ships it.
        <section className="clients-map-embed" aria-label="Clients' Map">
          <ClientsMapPanel />
        </section>
      ) : null}
      {currentUser?.isSuperAdmin || currentUser?.isAdmin ? (
        <AdminSection />
      ) : currentUser?.isContact ? (
        <ContactSection />
      ) : (
        <h1>Where care takes direction.</h1>
      )}
    </div>
  );

  return (
    <Layout showSideNav={false} hideSelectors={currentUser?.isContact}>
      {content}
    </Layout>
  );
}

export default SelectClientHomepage;
